<template>
  <div id="fusion_rey_conversation_list_container" class="w-full h-full flex flex-col">

    <!-- Search. Outside the scroll container on purpose: it filters the whole list, so it must stay
         put while the list moves under it. Hidden while loading or errored — there is nothing to
         filter and an enabled box that does nothing is worse than no box. Markup mirrors the
         messenger search in Navbar so the two read as the same control. -->
    <!-- border-t separates this surface from the navbar above it, which has no bottom edge of its
         own — without it the search box floats in the navbar's background with nothing marking where
         the conversation list starts. border-separator is the same subtle token the section bands
         and row rules use, so the list reads as one ruled surface rather than three borrowed styles.
         pt-3 (not pt-1) keeps the input off that new line; it matches the section bands' top pad. -->
    <!-- That border-t has since been removed: the navbar now draws its own bottom rule on Spark
         (FusionBody, show_spark_navbar_rule), and this list only renders on Spark once the splash is
         gone, which is exactly when that rule is up. The two stacked into a doubled line. pt-3 stays
         for the same reason as before, keeping the input off the line above it. -->
    <div v-if="show_sections" class="fusion-conversation-search-bar flex-shrink-0 pl-[18px] pr-2 pt-3 pb-2">
      <div class="relative w-full">
        <i class="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-icon-subtle text-lg pointer-events-none" />
        <input v-model="search_query"
               type="text"
               autocomplete="off"
               :placeholder="$t('rey.conversations.searchPlaceholder')"
               :aria-label="$t('rey.conversations.searchPlaceholder')"
               class="fusion-conversation-search-input w-full h-10 pl-10 pr-9 rounded-xl border border-border-default bg-surface-1 text-base text-text-default placeholder:text-text-placeholder outline-none transition-[border-color,box-shadow] focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]" />
        <button v-if="search_query"
                type="button"
                :aria-label="$t('common.clearSearch')"
                class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center text-icon-subtle cursor-pointer opacity-80 hover:opacity-100"
                @click.prevent="clearSearch">
          <i class="ri-close-circle-line text-base" />
        </button>
      </div>

      <!-- What the Recent cap does, said once up front. Inside the search bar rather than above the
           first section band: it describes the list as a whole, and a line sitting inside a section
           would read as being about that section only. It therefore also hides with the search box
           while the list is loading or failed, when there is no list to describe.
           text-text-subtle keeps it the same grey as the row subtext rather than a fourth one.
           text-[11px] sits deliberately between the two sizes around it — a step down from the row
           subtext (12px) so the hint reads as chrome, but a step up from the section labels and the
           dealer pill (10px), which are single words and can take a size a wrapping sentence should
           not. It is the one size in this file that is its own value, and that is the reason. -->
      <p class="fusion-conversation-cap-hint text-text-subtle text-[11px] font-normal leading-snug pt-2 pr-1">
        {{ $t('rey.conversations.capHint', { max: recent_conversation_cap }) }}
      </p>
    </div>

    <div ref="conversation_scroller" class="flex-1 overflow-y-auto pl-[18px] pr-2 pb-3 min-h-0">

      <!-- Loading -->
      <p v-if="is_loading_spark_conversations && !$FUSarrayHasEntries(spark_conversations)"
         class="text-center text-text-default/70 my-5">
        {{ $t('rey.conversations.loading') }}
      </p>

      <!-- Load failed. Offers a retry rather than silently showing an empty list, which would read
           as "you have no conversations" — the one wrong thing to tell someone whose conversations
           we simply could not fetch. -->
      <div v-else-if="load_error" class="w-full h-full flex flex-col items-center justify-center px-6 text-center">
        <p class="text-text-placeholder text-base leading-snug mb-3">{{ $t('rey.conversations.loadError') }}</p>
        <button type="button"
                class="h-10 px-4 rounded-xl bg-btn-secondary text-text-default text-base font-semibold cursor-pointer"
                @click.prevent="getConversations">
          {{ $t('rey.conversations.retry') }}
        </button>
      </div>

      <!-- Sections. One loop rather than three copies of the row: the row markup below is the only
           copy of it, so a change to a row cannot land in Recent and miss Pinned. Every section
           renders its divider unconditionally — an empty Pinned still shows its band, which is what
           tells the user the section exists at all. -->
      <template v-else>
        <template v-for="section in sections" :key="`fusion_rey_section_${section.key}`">

          <!-- Section divider. Negative margins cancel the scroll container's padding so the band
               spans the full width and reads as a divider rather than an inset label. Sticky so the
               heading stays visible while its own rows scroll under it; z-[1] keeps it below the
               row action menu (z-10), which must never be covered by the next heading.
               Closed on both edges. The last row of a section drops its own bottom rule, so without
               a top border here a heading butts straight onto the rows above it with nothing between
               them. Every section gets the same treatment rather than just the first: the three come
               out of one loop and are meant to be the same object, and a heading styled differently
               from its siblings reads as a different KIND of thing. border-border-separator already
               colours every edge, so the top line cannot drift from the bottom one. -->
          <div class="fusion-conversation-section sticky top-0 z-[1] -ml-[18px] -mr-2 pl-[18px] pr-3 pt-3 pb-[5px] flex flex-col gap-[3px] bg-surface-page border-t border-b border-border-separator">
            <div class="flex items-center justify-between gap-2">
              <div class="fusion-conversation-section-label font-sans text-[10px] font-bold uppercase tracking-[0.08em] text-text-subtle">
                {{ $t(section.labelKey) }}
              </div>
              <div class="fusion-conversation-section-count font-sans text-[10px] text-text-placeholder">{{ section.rows.length }}</div>
            </div>
          </div>

          <!-- Section empty state. A search that matches nothing says so rather than reusing the
               section's "nothing here yet" line — the two mean different things and only one of them
               is worth acting on. -->
          <div v-if="!section.rows.length" class="py-3">
            <p class="text-text-placeholder text-sm leading-snug">{{ sectionEmptyLabel(section) }}</p>
            <p v-if="section.key === 'recent' && !search_active" class="text-text-placeholder text-sm leading-snug">
              {{ $t('rey.conversations.emptyHint') }}
            </p>
          </div>

          <!-- Full-bleed, the same way the section band above is: the negative margins cancel the
               scroll container's padding and the matching pl/pr put the CONTENT back where it was,
               so only the background, the hover and the bottom rule get wider. A row is a hit target
               for the whole width of the panel, and a highlight that stopped short of the edges made
               it look like it was not.
               No w-full, and that is the load-bearing part: an explicit width:100% is already sized
               to the container's content box, so negative margins cannot widen it — they only shift
               it left, which breaks the right edge instead of fixing it. Width has to stay auto for
               this to resolve to "container + 26px", which is why the band above carries no width
               either. -->
          <div v-for="(conversation, idx) in section.rows"
             :key="`fusion_rey_conversation_${section.key}_${conversation.threadId}`"
             class="fusion-conversation-row flex gap-2 items-start -ml-[18px] -mr-2 pl-[18px] pr-2 bg-surface-1 hover:bg-black/5 cursor-pointer"
             :class="[
               { 'bg-black/5': conversation.threadId === spark_active_thread_id },
               idx < section.rows.length - 1 ? 'border-b border-border-separator' : ''
             ]"
             @click.prevent="openConversation(conversation.threadId)">

          <!-- Title, then timestamp + preview as subtext beneath it. The dot separator is the same
               one MessageTimestamp uses between a sender and a time, so the two subtexts read as one
               line rather than two competing labels. -->
          <div class="flex-1 flex flex-col gap-0.5 min-w-0 py-4">
            <!-- Inline rename. @click.stop and @keydown.stop keep the row handler and any parent
                 shortcut out of it: without them a click to position the caret would open the
                 conversation, and typing would fall through to the surrounding surface. -->
            <input v-if="renaming_thread_id === conversation.threadId"
                   :ref="`rename_input_${conversation.threadId}`"
                   v-model="rename_draft"
                   type="text"
                   :maxlength="rename_max_length"
                   :disabled="is_renaming"
                   :aria-label="$t('rey.conversations.rename')"
                   class="fusion-conversation-rename-input w-full bg-surface-1 border border-border-default rounded-lg px-2 py-1 text-text-default text-sm font-medium outline-none focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
                   :class="{ 'opacity-60': is_renaming }"
                   @click.stop
                   @keydown.stop
                   @keydown.enter.prevent="commitRename"
                   @keydown.esc.prevent="cancelRename"
                   @blur="commitRename" />
            <div v-else class="flex items-center gap-1.5 min-w-0">
              <!-- Pin marker. LEFT of the title rather than trailing it so it reads as a property of
                   the conversation and lines up down the column as the eye scans the list. aria-hidden
                   because the row already sits under a "Pinned" heading that tells a screen reader the
                   same thing — the title attribute here is a mouse tooltip only. flex-shrink-0 keeps
                   the icon whole and leaves the TITLE to absorb the truncation. -->
              <i v-if="isPinned(conversation)"
                 class="fusion-conversation-pin-icon ri-pushpin-fill text-text-subtle text-xs flex-shrink-0"
                 aria-hidden="true"
                 :title="$t('rey.conversations.pinned')" />
              <span class="fusion-conversation-title text-text-default/80 text-sm truncate"
                    :class="isUnread(conversation) ? 'font-semibold' : 'font-medium'">{{ displayTitle(conversation) }}</span>
              <!-- Unread marker. A dot rather than a count: what arrives here is a morning brief or a
                   delivered task result, and "3" would invite the reader to look for three of something
                   when the answer is "there is something new". The bold title above carries the same
                   fact at a glance, which is what makes the list scannable without hunting for dots.
                   RIGHT of the title, unlike the pin: the pin is a property of the conversation and
                   lines up down the column, this is a property of its CONTENTS and follows the name.
                   flex-shrink-0 so the title keeps absorbing the truncation, and a real label rather
                   than aria-hidden — nothing else in the row tells a screen reader this. -->
              <span v-if="isUnread(conversation)"
                    class="fusion-conversation-unread-dot w-2 h-2 rounded-full bg-btn-primary flex-shrink-0"
                    role="img"
                    :aria-label="$t('rey.conversations.unread')"
                    :title="$t('rey.conversations.unread')" />
            </div>
            <div class="flex items-center gap-1.5 min-w-0">
              <span v-if="conversation.lastMessageAt" class="text-text-subtle text-xs font-normal flex-shrink-0">
                {{ formatConversationDate(conversation.lastMessageAt) }}
              </span>
              <!-- Dealership pill: the store this conversation's answers came from, which is not
                   necessarily the store the host page is on right now. It sits between the timestamp
                   and the preview because it qualifies the CONVERSATION rather than the message, and
                   because the preview has to keep the end of the line — that is the part allowed to
                   truncate. Rendered only when a label resolves: a conversation nobody has asked
                   anything yet has no dealer to name, and an empty chip is worse than no chip.
                   A block box, NOT the inline-flex the otherwise-identical MessageThread pill is: this
                   one truncates, and text-overflow has nothing to apply to when the box's own content
                   is flex items rather than text. max-w with its own truncate makes a long store name
                   give ground before the preview vanishes, since flex-shrink-0 otherwise lets it push;
                   title carries the whole name back for the truncated case.
                   --background-layer-02 is a BACKGROUND token, which is the right kind for a filled
                   chip — the inverse of the mistake the dot below documents. -->
              <span v-if="dealer_labels[conversation.threadId]"
                    class="fusion-conversation-dealer-pill inline-block flex-shrink-0 max-w-[45%] px-2 py-0.5 rounded-full bg-[var(--background-layer-02)] text-text-subtle text-[10px] font-medium leading-4 truncate"
                    :title="dealer_labels[conversation.threadId]">
                {{ dealer_labels[conversation.threadId] }}
              </span>
              <!-- bg-text-placeholder, not the bg-surface-3 this was copied from: surface-3 is a
                   BACKGROUND token (gray-10 light, gray-90 dark), so it tracked the page behind the
                   dot instead of the text beside it and came out nearly invisible in both themes.
                   A foreground token inverts correctly. Placeholder rather than text-subtle — one
                   step lighter than the timestamp and preview it sits between, so the dot reads as
                   punctuation rather than as a third character in the line. -->
              <div v-if="conversation.lastMessageAt && conversation.lastMessagePreview"
                   class="fusion-conversation-row-dot w-[2.87px] h-[2.87px] bg-text-placeholder rounded-full flex-shrink-0"></div>
              <span class="text-text-subtle text-xs font-normal truncate">
                {{ conversation.lastMessagePreview || $t('rey.conversations.noMessages') }}
              </span>
            </div>
          </div>

          <!-- Row actions. @click.stop everywhere inside: without it every click in here would also
               hit the row handler and open the conversation behind the menu.
               Absent entirely on the Scheduled tasks thread: all three items are meaningless there.
               It is system-owned, so there is no name to change; it never rotates out of the list, so
               pinning it would protect it from nothing; and deleting it would only mean the next run
               silently made another one. An empty menu behind a button is worse than no button. -->
          <div v-if="!isScheduled(conversation)" class="relative flex-shrink-0 py-4">
            <div class="fusion-conversation-row-menu-toggle h-8 w-8 flex items-center justify-center rounded-xl cursor-pointer transition-colors hover:bg-button-ghost-hover active:bg-button-ghost-active"
                 role="button"
                 aria-haspopup="menu"
                 :aria-expanded="open_menu_thread_id === conversation.threadId ? 'true' : 'false'"
                 :aria-label="$t('rey.conversations.rowActions')"
                 :title="$t('rey.conversations.rowActions')"
                 @click.stop="toggleRowMenu(conversation.threadId)">
              <i class="ri-more-fill ri-125" />
            </div>
            <div v-if="open_menu_thread_id === conversation.threadId"
                 ref="row_menu"
                 role="menu"
                 class="absolute right-0 top-full mt-1 bg-surface-1 border border-border-separator rounded-xl shadow-md z-10 w-max py-1"
                 @click.stop>
              <div role="menuitem"
                   class="flex items-center gap-2 px-3 py-2 text-sm text-text-default cursor-pointer hover:bg-button-ghost-hover rounded-lg mx-1 whitespace-nowrap"
                   @click.stop="onRowAction('rename', conversation)">
                {{ $t('rey.conversations.rename') }}
              </div>
              <div role="menuitem"
                   class="flex items-center gap-2 px-3 py-2 text-sm text-text-default cursor-pointer hover:bg-button-ghost-hover rounded-lg mx-1 whitespace-nowrap"
                   @click.stop="onRowAction('pin', conversation)">
                {{ isPinned(conversation) ? $t('rey.conversations.unpin') : $t('rey.conversations.pinToTop') }}
              </div>
              <div class="my-1 border-t border-border-separator"></div>
              <div role="menuitem"
                   class="flex items-center gap-2 px-3 py-2 text-sm text-danger cursor-pointer hover:bg-button-ghost-hover rounded-lg mx-1 whitespace-nowrap"
                   @click.stop="onRowAction('delete', conversation)">
                {{ $t('rey.conversations.delete') }}
              </div>
            </div>
          </div>
          </div>
        </template>
      </template>
    </div>

    <!-- Delete confirmation. Deliberately a real dialog rather than an inline "are you sure" row:
         this is irreversible and it destroys the conversation's messages and uploaded files. -->
    <dialog-modal :is-open="Boolean(pending_delete)" :title="$t('rey.conversations.deleteTitle')"
                  :show-close-button="true" :processing="is_deleting_conversation"
                  :close-on-backdrop-click="!is_deleting_conversation"
                  @close="cancelDelete">
      <!-- No padding of its own: DialogModal already wraps each slot in its own padding
           (contentClasses `p-6 pt-2`, footerClasses `p-6 pt-4`). Adding more here stacked on top of
           that and left the buttons marooned in the middle of the panel. The negative margins below
           trim the wrapper's generous defaults for this one small confirm dialog without touching
           DialogModal, which every other dialog in the app shares. -->
      <div class="flex flex-col gap-2 -mt-1">
        <p class="text-text-default text-base font-semibold leading-snug">{{ pending_delete_title }}</p>
        <p class="text-text-subtle text-sm leading-snug">{{ $t('rey.conversations.deleteBody') }}</p>
        <p v-if="delete_error" class="text-danger text-sm leading-snug">{{ $t('rey.conversations.deleteError') }}</p>
      </div>
      <template #footer>
        <div class="w-full flex items-center justify-end gap-2 -mx-2 -mb-2 -mt-1">
          <button type="button"
                  class="h-10 px-4 rounded-xl bg-btn-secondary text-text-default text-base font-semibold"
                  :class="is_deleting_conversation ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
                  :disabled="is_deleting_conversation"
                  @click.prevent="cancelDelete">
            {{ $t('common.cancel') }}
          </button>
          <!-- The danger BUTTON tokens, not `bg-danger`: Tailwind maps `danger` to --text-error (a
               text colour), so bg-danger would paint error-text red rather than the button red, and
               there is no btn-danger mapping. These two have dark-mode overrides in tokens.css, so
               the button stays theme-aware. -->
          <button type="button"
                  class="h-10 px-4 rounded-xl bg-[var(--button-danger-default)] text-[var(--text-button-danger)] text-base font-semibold flex items-center gap-2 hover:opacity-90"
                  :class="is_deleting_conversation ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
                  :disabled="is_deleting_conversation"
                  :aria-busy="is_deleting_conversation ? 'true' : 'false'"
                  @click.prevent="confirmDelete">
            <div v-if="is_deleting_conversation" class="fusion-loader loader-sm inline-block"></div>
            {{ $t('rey.conversations.delete') }}
          </button>
        </div>
      </template>
    </dialog-modal>

    <!-- Recent-cap confirmation, raised when an UNPIN would push Recent past the cap. Separate from
         the delete dialog above rather than a second mode on it: the two name different
         conversations (that one names the row the user clicked, this one names a row further down
         the list they did not), and the confirm here resumes the unpin afterwards. -->
    <rey-conversation-cap-dialog :is-open="Boolean(pending_cap_unpin)"
                                 mode="unpin"
                                 :victim-title="cap_victim_title"
                                 :processing="is_resolving_cap"
                                 :error="cap_error"
                                 @confirm="confirmCapUnpin"
                                 @cancel="cancelCapUnpin" />
  </div>
</template>

<script>
  import { mapStores } from 'pinia';
  import { useMainStore } from '@store/main';
  import DialogModal from '@components/UI/DialogModal.vue';
  import ReyConversationCapDialog from '@components/Rey/ReyConversationCapDialog.vue';
  import { RECENT_CONVERSATION_CAP, oldestUnpinnedRow, wouldExceedRecentCap } from '../../utils/reyConversationCap';
  import { ALL_ROOFTOPS_GROUP_ID } from '../../utils/dealerGroups';

  // Matches PageContext.ContextSystemIdFusion server-side, and the literal MessengerWindow.getAiThread
  // already passes to GetAIThread — the client has no shared constant for it.
  const SYSTEM_ID_FUSION = 'Fusion';

  // Clearance left under a row menu scrolled into view, so its shadow is not clipped by the edge it
  // was just brought up to. Same value FusionDropdown uses for its panel.
  const ROW_MENU_VIEW_GUTTER = 5;

  /**
   * The conversation list the Spark hamburger swaps in for the chat window.
   *
   * The rows live in the store (`spark_conversations`) rather than in this component so the chat
   * window can keep them current — a new conversation, or a generated title arriving over the
   * socket — while the list is unmounted. Selecting a row hands off to MessengerWindow over the
   * bus for the same reason MESSENGER-OPEN-THREAD exists: opening a thread means join + load +
   * nav state, and that belongs to the window that owns the message list.
   */
  export default {
    name: 'ReyConversationList',
    components: {
      'dialog-modal': DialogModal,
      'rey-conversation-cap-dialog': ReyConversationCapDialog,
    },
    mounted() {
      // Fetch on every mount rather than trusting whatever is in the store. The list is only
      // mounted while the user is looking at it, and that is exactly when they expect it to be
      // current — a title generated in another tab, or a conversation started on another device.
      this.getConversations();
      window.event_bus.$on('CLOSE-OVERLAYS', this.closeRowMenu);
      // Live updates while the list is open. A scheduled result or a morning brief is delivered by a
      // background run, so nothing the user did brings it in — without this the list they are looking
      // at stays as it was fetched until they navigate away and back.
      window.event_bus.$on('WS-MESSAGE', this.handleWebSocketMessage);
    },
    beforeUnmount() {
      window.event_bus.$off('CLOSE-OVERLAYS', this.closeRowMenu);
      window.event_bus.$off('WS-MESSAGE', this.handleWebSocketMessage);
      // closeRowMenu is what removes the document listener, and the menu can be open at unmount
      // (picking a conversation from behind it unmounts this list) — without this the handler
      // outlives the component.
      this.closeRowMenu();
    },
    data() {
      return {
        load_error: false,
        // Debounced refetch for deliveries that arrive while the list is open. Built on first use by
        // refreshConversationsSoon().
        refresh_conversations_debounced: null,
        // Thread id of the row whose action menu is open, or null. A single id rather than a
        // per-row flag so opening one menu implicitly closes any other.
        open_menu_thread_id: null,
        // The conversation awaiting delete confirmation, or null. Holds the row object rather than
        // just the id so the dialog can name it after the list has been filtered.
        pending_delete: null,
        is_deleting_conversation: false,
        delete_error: false,
        // Thread id of the row being renamed in place, or null. The draft is held separately from
        // the row so an abandoned edit never touches the cached list.
        renaming_thread_id: null,
        rename_draft: '',
        is_renaming: false,
        // Title filter. Component-local rather than store state: it is a view of the list, not part
        // of it, and it should not survive closing the list and reopening it.
        search_query: '',
        // The unpin being held up by the Recent cap, or null. Holds the row the user clicked; the
        // row to be DELETED is cap_victim, which is a different conversation further down the list.
        pending_cap_unpin: null,
        cap_victim: null,
        is_resolving_cap: false,
        cap_error: false,
      }
    },
    computed: {
      ...mapStores(useMainStore),
      spark_conversations: {
        get() { return this.mainStore.spark_conversations || []; },
        set(value) { this.mainStore.spark_conversations = value; }
      },
      // Live unread counts, keyed by thread id. Read-only here: MessengerWindow owns the writes (it
      // seeds them from the server on every thread fetch, bumps them off WebSocket frames, and zeroes
      // them when a thread is opened), and a second writer is how the two would drift.
      unreads_by_thread_id() {
        return this.mainStore.unreads_by_thread_id || {};
      },
      is_loading_spark_conversations: {
        get() { return this.mainStore.is_loading_spark_conversations; },
        set(value) { this.mainStore.is_loading_spark_conversations = value; }
      },
      spark_view: {
        get() { return this.mainStore.spark_view; },
        set(value) { this.mainStore.spark_view = value; }
      },
      // The Rey conversation open behind the list. The Spark panel's own mirror, not active_thread_id:
      // that one is Internal Messaging's, and in the toolbar it held whichever internal chat was open.
      spark_active_thread_id() {
        return this.mainStore.spark_active_thread_id;
      },
      all_dealers() {
        return this.mainStore.all_dealers || [];
      },
      // Only a message the owner sent reorders the list (see applyLiveLastMessage).
      current_user_guid() {
        return this.mainStore.user_access_info?.userId || null;
      },
      // Only ever needed to NAME a group-scoped conversation, so the list is fetched lazily rather than
      // on mount (see ensureDealerGroupsForLabels): a single-store user has no group to name, and the
      // store caches it for the picker anyway.
      dealer_groups() {
        return this.mainStore.dealer_groups || [];
      },
      // Every row's dealer-pill text, keyed by threadId — a lookup in the row markup rather than three
      // calls to a method that searches two arrays. Built from the store list, not the filtered or
      // sectioned views: every section renders the same row objects, and a label does not depend on
      // which section a conversation landed in.
      dealer_labels() {
        const labels = {};
        this.spark_conversations.forEach(conversation => {
          const label = this.resolveDealershipLabel(conversation);
          if (label) labels[conversation.threadId] = label;
        });

        return labels;
      },
      // Named from the snapshot taken when the menu item was clicked, not looked up in the list —
      // the row is removed from the list on success while the dialog is still closing.
      pending_delete_title() {
        return this.pending_delete ? this.displayTitle(this.pending_delete) : '';
      },
      // Named the same way as pending_delete_title, and from the same snapshot: the victim row is
      // removed from the list on success while the dialog is still closing, so looking it up again
      // would blank the name mid-animation.
      cap_victim_title() {
        return this.cap_victim ? this.displayTitle(this.cap_victim) : '';
      },
      // Matches ConversationTitleService.MaxTitleLength server-side. Enforced again on the server,
      // which trims and caps — this only stops the box accepting what would be silently truncated.
      rename_max_length() {
        return 60;
      },
      // Exposed as a computed purely so the template can interpolate it — the hint under the search
      // box states the number, and a second hardcoded 30 in the copy would be free to drift away from
      // the cap it describes.
      recent_conversation_cap() {
        return RECENT_CONVERSATION_CAP;
      },
      search_active() {
        return Boolean(this.search_query.trim());
      },
      // The search box and the sections appear together: both are views OF the list, and neither
      // means anything while it is loading or failed. Mirrors the template's v-if chain so the
      // header cannot render above a "couldn't load" message.
      show_sections() {
        if (this.load_error) return false;
        return !(this.is_loading_spark_conversations && !this.spark_conversations.length);
      },
      // Filtered on the DISPLAYED title, not the stored name. Two of the labels a user actually
      // reads — "Current Thread" and "New conversation" — are localized strings this component
      // substitutes for a name that is either an internal literal or empty, so matching the raw
      // field would make exactly those rows unfindable by the text on screen.
      filtered_conversations() {
        const query = this.search_query.trim().toLowerCase();
        if (!query) return this.spark_conversations;

        return this.spark_conversations.filter(
          conversation => this.displayTitle(conversation).toLowerCase().indexOf(query) >= 0);
      },
      // Pinned rows, in the order the list already arrived in — most recently used first, the same rule
      // every other section uses. So a pinned conversation you have just used sits at the top of its
      // own section. "Pin to top" means the top SECTION, not a hand-ordered slot: nothing in this list
      // lets a user order rows by hand, so a separate pin-time sort key would have no way to be seen.
      //
      // Derived from filtered_conversations, not the raw store list, so a search narrows every section
      // rather than leaving Pinned showing rows the query excluded. recent_conversations subtracts
      // exactly these rows by threadId, so a pin MOVES a conversation rather than duplicating it.
      pinned_conversations() {
        return this.filtered_conversations.filter(conversation => this.isPinned(conversation));
      },
      // The one thread scheduled results are delivered into, when the user has one.
      //
      // The product question this section was stubbed for is settled: a scheduled task is NOT a
      // conversation. All of a user's tasks deliver into a single account-wide thread, and this
      // section holds that one thread rather than a row per task — otherwise five daily tasks would
      // fill the drawer with threads nobody opens individually.
      //
      // Server-flagged (isScheduled), not matched on a naming convention here. Derived from
      // filtered_conversations like every other section, so a search narrows this one too.
      //
      // Still an array, and still usually of length one, because the section renders rows: making it
      // a single object would mean a second rendering path for the same kind of row.
      scheduled_conversations() {
        return this.filtered_conversations.filter(conversation => this.isScheduled(conversation));
      },
      // Everything not claimed by a section above. While those are empty this is the whole list.
      recent_conversations() {
        const claimed = new Set([
          ...this.pinned_conversations.map(row => row.threadId),
          ...this.scheduled_conversations.map(row => row.threadId),
        ]);

        return this.filtered_conversations.filter(conversation => !claimed.has(conversation.threadId));
      },
      // Order here is render order. Recent last so the sections the user curated sit above the ones
      // that simply accumulate.
      sections() {
        return [
          {
            key: 'pinned',
            labelKey: 'rey.conversations.sectionPinned',
            rows: this.pinned_conversations,
          },
          {
            key: 'scheduled',
            labelKey: 'rey.conversations.sectionScheduled',
            rows: this.scheduled_conversations,
          },
          {
            key: 'recent',
            labelKey: 'rey.conversations.sectionRecent',
            rows: this.recent_conversations,
          },
        ];
      },
    },
    methods: {
      getConversations() {
        const self = this;
        this.load_error = false;
        this.is_loading_spark_conversations = true;
        return this.mainStore.GetReyConversations(SYSTEM_ID_FUSION).then(response => {
          self.spark_conversations = Array.isArray(response) ? response : [];
          self.is_loading_spark_conversations = false;
          self.ensureDealerGroupsForLabels();
        }, error => {
          self.is_loading_spark_conversations = false;
          self.load_error = true;
          console.error('GetReyConversations failed:', error);
        });
      },
      // The dealership pill's text for one row, or null when there is nothing to name. Resolved the
      // way MessengerInput's active_dealer_label resolves the picker's own label, so the pill here and
      // the picker inside the conversation cannot word the same store two different ways.
      resolveDealershipLabel(conversation) {
        // The Scheduled tasks thread is account-wide: it collects the results of every task the user
        // owns, and those tasks each run against their own rooftop. So its scope is all of them, and
        // naming any single store on this row would be wrong rather than merely incomplete.
        // Stated rather than left blank — the pill is how a row declares which store its answers came
        // from, and this row has a real answer to that.
        if (this.isScheduled(conversation)) return this.$t('rey.conversations.allStores');

        const dealerId = conversation?.contextDealerId;
        if (!dealerId) return null;

        // Group scope. DealerType is the ONLY discriminator available: the id is a group id and it
        // arrives with contextDealerIdType deliberately null (the server-side resolver documents why),
        // so the id TYPE cannot be used to tell a group from a rooftop.
        if (String(conversation.contextDealerType || '').toLowerCase() === 'group') {
          if (dealerId === ALL_ROOFTOPS_GROUP_ID)
            return `${this.$t('messaging.input.allRooftops')} · ${this.all_dealers.length}`;

          // Null, not the raw id, when the group is unknown: a group id is an opaque guid that would
          // tell the user nothing. The label appears on its own once the groups load, because the row
          // reads it through a computed.
          const group = this.dealer_groups.find(candidate => candidate.id === dealerId);
          return group?.name || null;
        }

        // Single rooftop. Case-insensitive on the CIF for the same reason the store's current_cif
        // getter is: hosts do not agree on its casing, and a strict match would cost the row its name.
        const dealer = this.all_dealers.find(
          candidate => (candidate.cifNumber || '').toLowerCase() === String(dealerId).toLowerCase());

        // Falls back to the raw CIF rather than rendering nothing: a store missing from the caller's
        // access list is still the honest answer to "which dealership is this conversation using".
        return dealer?.name || dealer?.cifNumber || dealerId;
      },
      // A group-scoped row needs the caller's dealer groups to name its group; a rooftop-scoped one
      // resolves entirely out of access info the store already holds. So the fetch happens only when
      // the list actually contains a group-scoped conversation — the common single-store case makes no
      // request at all, and the store action caches, so a second list load makes no second one.
      ensureDealerGroupsForLabels() {
        const needsGroups = this.spark_conversations.some(
          conversation => String(conversation?.contextDealerType || '').toLowerCase() === 'group');
        if (needsGroups) this.fetchDealerGroups();
      },
      fetchDealerGroups() {
        this.mainStore.fetchDealerGroups();
      },
      clearSearch() {
        this.search_query = '';
      },
      // "No matches" only when a search is responsible for the emptiness. Without the distinction an
      // empty Pinned section would read as a failed search, and a search that found nothing would
      // read as "you have never pinned anything".
      //
      // Spelled out as literal $t() calls rather than a key carried on the section descriptor. The
      // i18n spec finds usages by scanning components for $t('...'), and a key it cannot see reads
      // as an orphan — the check that catches genuinely dead strings. `labelKey` below is exempt
      // because the scanner recognises that property name by convention; inventing a second one
      // would mean widening a shared guard to suit this file.
      sectionEmptyLabel(section) {
        if (this.search_active) return this.$t('rey.conversations.noMatches');
        if (section.key === 'pinned') return this.$t('rey.conversations.pinnedEmpty');
        if (section.key === 'scheduled') return this.$t('rey.conversations.scheduledEmpty');
        return this.$t('rey.conversations.empty');
      },
      toggleRowMenu(threadId) {
        // Same open/close mechanics as Navbar's more-options menu: the document listener is added
        // on the next tick so the click that opened the menu does not immediately close it.
        if (this.open_menu_thread_id === threadId) {
          this.closeRowMenu();
          return;
        }

        this.open_menu_thread_id = threadId;
        const self = this;
        this.$nextTick(() => {
          document.addEventListener('click', self.closeRowMenu);
          self.scrollRowMenuIntoView();
        });
      },
      // The menu is absolutely positioned under its toggle, so opening it on a row near the bottom of
      // the list leaves it hanging past the fold with its last item (Delete) out of reach. Scroll the
      // list by exactly the overflow so the whole menu is visible. A jump rather than a smooth scroll:
      // the menu should be usable the moment it appears, not after an animation settles.
      //
      // Measured against whichever edge is higher, the list's or the viewport's: the list fills its
      // panel, but the panel itself can run past the bottom of a short host iframe, and a menu inside
      // the list can still be off screen there.
      scrollRowMenuIntoView() {
        const ref = this.$refs.row_menu;
        // v-for refs are arrays in Vue 3.
        const menu = Array.isArray(ref) ? ref[0] : ref;
        const scroller = this.$refs.conversation_scroller;
        if (!menu || !scroller) return;

        const visibleBottom = Math.min(scroller.getBoundingClientRect().bottom, window.innerHeight);
        const overflow = menu.getBoundingClientRect().bottom - visibleBottom + ROW_MENU_VIEW_GUTTER;
        if (overflow <= 0) return;

        scroller.scrollTop += overflow;
      },
      closeRowMenu() {
        this.open_menu_thread_id = null;
        document.removeEventListener('click', this.closeRowMenu);
      },
      // Every menu item lands here so the menu is closed exactly once, before anything else runs —
      // a handler that opened a dialog or focused an input with the menu still up would leave it
      // floating over the thing it just opened. The trailing voids keep an UNRECOGNISED action quiet
      // for the linter rather than throwing at a user.
      onRowAction(action, conversation) {
        this.closeRowMenu();

        if (action === 'delete') {
          this.requestDelete(conversation);
          return;
        }
        if (action === 'rename') {
          this.startRename(conversation);
          return;
        }
        if (action === 'pin') {
          this.togglePin(conversation);
          return;
        }

        void action;
        void conversation;
      },
      startRename(conversation) {
        if (!conversation || !conversation.threadId) return;

        // Seeded with the RAW stored name, not displayTitle(): the placeholder labels ("New
        // conversation", "Current Thread") are localized UI strings, and pre-filling one would let a
        // user accidentally save the English label as the conversation's real name.
        //
        // A brief's derived date label is one of those strings, so an un-renamed brief opens empty
        // rather than pre-filled with "Morning brief · Mon, Sep 14". Saving that would turn a label
        // every viewer reads in their own locale into one stored in whichever locale happened to be
        // active when the box was opened.
        this.rename_draft = (conversation.name || '').trim();
        this.renaming_thread_id = conversation.threadId;

        const self = this;
        this.$nextTick(() => {
          // v-for refs are arrays in Vue 3.
          const ref = self.$refs[`rename_input_${conversation.threadId}`];
          const input = Array.isArray(ref) ? ref[0] : ref;
          if (!input) return;
          input.focus();
          input.select();
        });
      },
      cancelRename() {
        if (this.is_renaming) return;
        this.renaming_thread_id = null;
        this.rename_draft = '';
      },
      commitRename() {
        const self = this;
        const threadId = this.renaming_thread_id;
        if (!threadId || this.is_renaming) return;

        const name = (this.rename_draft || '').trim();
        const current = (this.spark_conversations || []).find(row => row.threadId === threadId);

        // Nothing typed, or nothing changed: close the editor without a round-trip. An empty name is
        // treated as a cancel rather than an error — the server rejects empty anyway, because empty
        // is what marks a conversation as "not yet named" and hands it back to the auto-titler.
        if (!name || name === (current && current.name ? current.name.trim() : '')) {
          this.cancelRename();
          return;
        }

        this.is_renaming = true;

        return this.mainStore.RenameReyConversation('Fusion', threadId, name).then(response => {
          self.is_renaming = false;
          self.renaming_thread_id = null;
          self.rename_draft = '';

          // Render what the SERVER stored, not what was typed — it trims and caps, so the two can
          // differ. The rename is also broadcast on the conversationTitle frame, which applies the
          // same patch in other tabs; this one covers the socket being down.
          const stored = (response && response.name) ? response.name : name;
          self.spark_conversations = (self.spark_conversations || [])
            .map(row => row.threadId === threadId ? { ...row, name: stored } : row);
        }, error => {
          self.is_renaming = false;
          // Stay in the editor with the text intact so the rename can be retried or escaped —
          // dropping back to the old title would look like it worked.
          console.error('RenameReyConversation failed:', error);
        });
      },
      // Read through a helper rather than inline so the section filter, the pin marker and the menu
      // label cannot end up disagreeing about what "pinned" means. Coerced to a real boolean because
      // the field is ABSENT on rows served before this feature (and on any optimistic row built
      // without it), and `undefined` in a template v-if reads fine but compares badly.
      isPinned(conversation) {
        return Boolean(conversation && conversation.isPinned);
      },
      // Whether this row is the Scheduled tasks thread. Coerced like isPinned above, and for the same
      // reason: the flag is absent on rows served before it existed, and `undefined` leaking into a
      // v-if reads as false anyway but makes the intent harder to see at the call site.
      isScheduled(conversation) {
        return Boolean(conversation && conversation.isScheduled);
      },
      /**
       * How many messages in this conversation the owner has not read.
       *
       * Two sources, reconciled here rather than merged: the row carries the count
       * <c>/ai/conversations</c> returned when the list was fetched, and the store carries the live one.
       * The LIVE one wins wherever it exists — it is the value a WebSocket frame bumps and the value
       * opening a thread zeroes, so preferring the row's would re-light a conversation the owner just
       * read. The row's is the fallback for the window where this list has been fetched but
       * MessengerWindow has not yet loaded its own thread lists, which is where the map comes from.
       *
       * hasOwnProperty, not `||`: a live zero is an answer, and the falsy check would discard it and
       * fall through to the stale row count.
       */
      unreadCount(conversation) {
        if (!conversation || !conversation.threadId) return 0;

        const live = this.unreads_by_thread_id;
        if (Object.prototype.hasOwnProperty.call(live, conversation.threadId))
          return Number(live[conversation.threadId]) || 0;

        return Number(conversation.unreadCount) || 0;
      },
      /** Whether to mark this row as carrying something new — the dot and the bolder title. */
      isUnread(conversation) {
        return this.unreadCount(conversation) > 0;
      },
      /**
       * Keeps the open list current when a message arrives for one of its threads — a background
       * delivery, a finished Rey answer, or a send from another tab or device.
       *
       * A known row's unread count is already live through the store, because MessengerWindow stays
       * mounted behind this list (v-show, not v-if) and keeps handling frames for it; what that does
       * not cover is the row's timestamp, preview and position, which applyLiveLastMessage patches from
       * the frame. Only a thread this list has NO ROW FOR is refetched — a morning brief is created by
       * the delivery run itself, so the first the client hears of it is a message arriving for a thread
       * it has never seen.
       *
       * The refetch is debounced because a run delivers several results at once, and because every
       * frame that is NOT about this list (a DM, a channel) also lands here and costs one comparison.
       */
      handleWebSocketMessage(data) {
        if (!data || !data.threadId) return;
        if (['message', 'streamStart'].indexOf(data.type) < 0) return;
        if (this.spark_conversations.some(row => row && row.threadId === data.threadId)) {
          // Without this the dot lit up and the subtext beside it went on describing the previous
          // message until the list was reopened.
          this.applyLiveLastMessage(data);
          return;
        }

        this.refreshConversationsSoon();
      },
      /**
       * Moves a known row's timestamp and preview onto the message that just arrived for it, so the
       * subtext updates in the same frame as the unread dot rather than on the next fetch.
       *
       * Picks the message by the rule the conversations endpoint uses (GetUserObjectThreadsByType): the
       * newest with content that is not a system-role message. What shows now is therefore what the
       * next fetch returns, and the row does not change a second time when the list is reopened.
       *
       * Only a finished 'message' qualifies. A streamStart carries no answer yet — the answer it starts
       * arrives, once complete, as the persisted re-broadcast (isRebroadcast), which is a 'message' and
       * lands here. A delivered scheduled result is a plain 'message' with the result text as content.
       *
       * The list is ordered most-recently-USED first, by the owner's own last message
       * (MessengerApiFunctions.ReyConversationLastUsedAt), so only a message the owner SENT moves the
       * row — to the front of the store list, where the server would put it. That frame is one from
       * another tab or device, since sending from here means the chat, not this list, is on screen.
       * A Rey answer or a scheduled delivery updates the subtext in place: moving on those would
       * reshuffle the list on activity the owner did not cause.
       *
       * Every section is a filter over the store list in its own order, so the front of the list is
       * the top of whichever section the row belongs to — a pinned row rises within Pinned, never out
       * of it. That also keeps the Recent cap honest: oldestUnpinnedRow evicts the LAST unpinned row on
       * the assumption that list order is usage order.
       *
       * A frame older than what the row already shows is ignored, and neither patches nor moves it,
       * because a socket reconnect can replay one.
       */
      applyLiveLastMessage(data) {
        if (!data || data.type !== 'message') return;
        if (!data.content || data.role === 'system') return;

        const preview = this.buildMessagePreview(data.content);
        if (!preview) return;

        const rows = this.spark_conversations;
        const row = rows.find(candidate => candidate && candidate.threadId === data.threadId);
        if (!row) return;

        const sentAt = data.sentAt || new Date().toISOString();
        if (row.lastMessageAt && new Date(sentAt) < new Date(row.lastMessageAt)) return;

        const updated = { ...row, lastMessageAt: sentAt, lastMessagePreview: preview };
        const sentByOwner = Boolean(this.current_user_guid) && data.senderId === this.current_user_guid;
        this.spark_conversations = sentByOwner
          ? [updated, ...rows.filter(candidate => candidate !== row)]
          : rows.map(candidate => candidate === row ? updated : candidate);
      },
      /**
       * A row's preview text, built the way MessengerApiFunctions.BuildMessagePreview builds it
       * server-side: markdown stripped (links and images dropped whole), newlines collapsed, capped at
       * 120 characters. Kept to the server's rules rather than MessengerWindow.updateThreadPreview's
       * looser ones so a live preview and a fetched one never disagree about the same message.
       */
      buildMessagePreview(content) {
        if (!content) return '';

        const plain = String(content)
          .replace(/#{1,6}\s*|(\*\*|__)(.*?)\1|(\*|_)(.*?)\3|`(.+?)`|!?\[.*?\]\(.*?\)/g,
            (match, boldMark, boldText, italicMark, italicText, codeText) => {
              if (boldText !== undefined) return boldText;
              if (italicText !== undefined) return italicText;
              if (codeText !== undefined) return codeText;
              return '';
            })
          .replace(/\n/g, ' ')
          .replace(/\r/g, '')
          .trim();

        return plain.length > 120 ? plain.slice(0, 120) + '…' : plain;
      },
      // Built on first use and cached on the instance, the same way MessengerWindow.refreshObjectThreadsSoon
      // is: a debounced function in methods() would be shared by every instance of the component, and
      // rebuilding it per frame would reset the window each time and defeat the debounce.
      refreshConversationsSoon() {
        if (!this.refresh_conversations_debounced)
          this.refresh_conversations_debounced = this.$FUSdebounce(this.getConversations, 1200);

        this.refresh_conversations_debounced();
      },
      isBrief(conversation) {
        return Boolean(conversation && conversation.isBrief);
      },
      /**
       * A brief row's label: the day it covers, in the viewer's locale.
       *
       * Parsed into local parts rather than handed to Date() whole. A bare date string is read as UTC,
       * which shifts it back a day for anyone west of Greenwich — so a brief produced on the owner's
       * Monday would sit in the list labelled Sunday. The same trap the thread's own day separators
       * document.
       *
       * Falls back to a generic label rather than an empty row when the date is missing or malformed,
       * which is true of nothing this code writes but is cheap to survive.
       */
      briefTitle(conversation) {
        const dayKey = (conversation && conversation.briefDate ? conversation.briefDate : '').trim();
        if (!dayKey) return this.$t('rey.conversations.briefThread');

        const parts = dayKey.split('-').map(Number);
        if (parts.length !== 3 || parts.some(Number.isNaN)) return this.$t('rey.conversations.briefThread');

        const [year, month, day] = parts;
        const date = new Date(year, month - 1, day);
        if (Number.isNaN(date.getTime())) return this.$t('rey.conversations.briefThread');

        return this.$t('rey.conversations.briefThreadOn', {
          date: date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
        });
      },
      // Optimistic: the row jumps sections on click and only goes back if the server refuses. Pinning
      // is cheap, reversible and its whole point is the visible move, so waiting on a round-trip would
      // make the menu item look dead on a slow connection.
      //
      // Sends the DESIRED state, not a toggle — the server takes it the same way. A toggle would flip
      // whatever the server happened to hold, so a double click or a second tab could settle on the
      // opposite of what was clicked.
      togglePin(conversation) {
        const self = this;
        if (!conversation || !conversation.threadId) return;

        const threadId = conversation.threadId;
        const pinned = !this.isPinned(conversation);

        // An UNPIN moves a conversation into Recent, so it can push that section past its cap the
        // same way creating one does. Pinning is never gated: it only ever takes a row out.
        //
        // Counted against the whole store list, not recent_conversations — that one is derived from
        // the search-filtered list, so counting it would let the cap be walked straight past by
        // typing in the search box first.
        if (!pinned && wouldExceedRecentCap(this.spark_conversations, 1)) {
          const victim = oldestUnpinnedRow(this.spark_conversations);
          // No victim means there is nothing the cap could free, so the dialog would be asking a
          // question with no useful answer. Let the unpin through and leave the ceiling to the
          // server, which refuses independently.
          if (victim) {
            this.cap_error = false;
            this.cap_victim = victim;
            this.pending_cap_unpin = conversation;
            return;
          }
        }

        this.applyPinned(threadId, pinned);

        return this.mainStore.SetReyConversationPinned(SYSTEM_ID_FUSION, threadId, pinned).then(() => {
          // Nothing to apply: the patch above already holds the state the server accepted, and the
          // response carries no row. Other tabs get it over the conversationPinned frame, which
          // MessengerWindow applies to this same store list.
        }, error => {
          // Put the row back where the user found it. A pin that silently did not stick is worse than
          // one they can see bounce.
          self.applyPinned(threadId, !pinned);
          console.error('SetReyConversationPinned failed:', error);
        });
      },
      // Patches the cached row rather than refetching, exactly as the rename and delete paths do: the
      // list is store-owned, and a refetch would also reorder rows under the user mid-click.
      applyPinned(threadId, pinned) {
        this.spark_conversations = (this.spark_conversations || [])
          .map(row => row.threadId === threadId ? { ...row, isPinned: pinned } : row);
      },
      cancelCapUnpin() {
        // Ignored mid-delete for the same reason cancelDelete is: the delete is already in flight
        // server-side and cannot be called off, so closing the dialog here would hide an operation
        // that is still going to happen.
        if (this.is_resolving_cap) return;
        this.pending_cap_unpin = null;
        this.cap_victim = null;
        this.cap_error = false;
      },
      // Deletes the victim FIRST, then resumes the unpin. That order is what keeps the cap true at
      // every moment: unpinning first would leave Recent over the cap for as long as the delete
      // takes, and a delete that then failed would leave it over the cap for good.
      confirmCapUnpin() {
        const self = this;
        const conversation = this.pending_cap_unpin;
        const victim = this.cap_victim;
        if (!conversation || !victim || !victim.threadId || this.is_resolving_cap) return;

        const victimId = victim.threadId;
        this.is_resolving_cap = true;
        this.cap_error = false;

        // The same store action the manual delete uses, so the thread and its Cosmos documents go
        // the same way — this is that delete, aimed at a row the user did not click.
        return this.mainStore.DeleteThread(victimId).then(() => {
          self.is_resolving_cap = false;
          self.pending_cap_unpin = null;
          self.cap_victim = null;

          // Same local drop and the same belt-and-braces broadcast handling as confirmDelete.
          self.spark_conversations = (self.spark_conversations || [])
            .filter(row => row.threadId !== victimId);

          if (self.spark_active_thread_id === victimId)
            window.event_bus.$emit('SPARK-CONVERSATION-DELETED', victimId);

          // Re-entered rather than continued inline: the victim row is gone, so the cap check at
          // the top of togglePin now passes and the unpin takes its ordinary path — one copy of the
          // optimistic move and its round-trip, not two. If the list were somehow more than one row
          // over the cap, this simply reopens the dialog for the next-oldest row instead of letting
          // the unpin through.
          self.togglePin(conversation);
        }, error => {
          self.is_resolving_cap = false;
          // Dialog stays open with the error, and the unpin does NOT happen: the confirmation
          // exists to keep the cap honoured, and proceeding on a failed delete would break exactly
          // the thing the user was asked about.
          self.cap_error = true;
          console.error('DeleteThread failed:', error);
        });
      },
      requestDelete(conversation) {
        if (!conversation || !conversation.threadId) return;
        this.delete_error = false;
        this.pending_delete = conversation;
      },
      cancelDelete() {
        // Ignored mid-request: the delete is already in flight server-side and cannot be called off,
        // so closing the dialog here would hide an operation that is still going to happen.
        if (this.is_deleting_conversation) return;
        this.pending_delete = null;
        this.delete_error = false;
      },
      confirmDelete() {
        const self = this;
        const conversation = this.pending_delete;
        if (!conversation || !conversation.threadId || this.is_deleting_conversation) return;

        const threadId = conversation.threadId;
        this.is_deleting_conversation = true;
        this.delete_error = false;

        return this.mainStore.DeleteThread(threadId).then(() => {
          self.is_deleting_conversation = false;
          self.pending_delete = null;

          // Drop the row locally rather than refetching. The server also broadcasts threadDeleted,
          // which MessengerWindow.handleThreadDeleted applies to this same store list — so this is
          // belt and braces for the case where the socket is down, and the filter is idempotent.
          self.spark_conversations = (self.spark_conversations || [])
            .filter(row => row.threadId !== threadId);

          // If the deleted conversation was the one open behind this list, the chat surface is now
          // pointing at a thread that no longer exists. MessengerWindow owns that recovery (it drops
          // into a fresh draft) and reaches it through the same handler the broadcast uses; emitting
          // here means it happens even when the broadcast does not arrive.
          if (self.spark_active_thread_id === threadId)
            window.event_bus.$emit('SPARK-CONVERSATION-DELETED', threadId);
        }, error => {
          self.is_deleting_conversation = false;
          // Dialog stays open with the error — a failed delete the user cannot see is worse than a
          // visible one, and the retry is one click.
          self.delete_error = true;
          console.error('DeleteThread failed:', error);
        });
      },
      openConversation(threadId) {
        if (!threadId) return;
        // Spark-scoped event, NOT MESSENGER-OPEN-THREAD: in the toolbar layout MessengerWindow is
        // mounted twice and that handler returns early unless page_type === 'InternalMessaging',
        // so reusing it here would be ignored by the only instance that should act.
        window.event_bus.$emit('SPARK-OPEN-CONVERSATION', threadId);
        this.spark_view = 'Chat';
      },
      displayTitle(conversation) {
        // The Scheduled tasks thread is created with an empty name and never titled — the titler only
        // runs on conversations, and this one has no first question to be named from. Its label is
        // localized here rather than written into the database for the same reason an unnamed
        // conversation's is: an English string stored server-side could not be translated.
        // Checked before the empty-name branch below, which would otherwise call it untitled.
        if (this.isScheduled(conversation)) return this.$t('rey.conversations.scheduledThread');

        // A morning brief is named for the day it covers, and it is named HERE for the same reason: the
        // thread is written with an empty name, and a date formatted server-side would arrive in one
        // locale's wording. The date itself travels as yyyy-MM-dd, which is what makes it formattable.
        //
        // Also checked before the empty-name branch, which would otherwise call it untitled.
        //
        // UNLESS ITS OWNER RENAMED IT. A brief is an ordinary Rey conversation in every other respect
        // — it counts toward Recent, it can be pinned, it can be deleted — so it can be named too, and
        // a name someone chose outranks one we derived. The trade is real and it is theirs to make: the
        // date label is rendered in each viewer's locale and sorts the mornings against each other,
        // and a typed name does neither.
        if (this.isBrief(conversation)) {
          const chosen = (conversation && conversation.name ? conversation.name : '').trim();
          return chosen || this.briefTitle(conversation);
        }

        const name = (conversation && conversation.name ? conversation.name : '').trim();
        if (!name) return this.$t('rey.conversations.untitled');

        // The single thread every user had before multi-conversation carries the server-built literal
        // "Spark AI: {systemId}" as its name. It gets its OWN label rather than the untitled
        // placeholder: the titler only names conversations that have no name, so this one keeps that
        // literal forever and would otherwise sit in the list looking like an empty new conversation
        // while actually holding all of the user's history. Renaming it server-side was rejected —
        // that is a migration rewriting a field the user never set.
        if (name.indexOf('Spark AI:') === 0) return this.$t('rey.conversations.legacyThread');

        return name;
      },
      // Same shape as MessageTimestamp.formatMessageDate: clock time for today, the localized
      // "Yesterday" for yesterday, M/D/YYYY before that.
      formatConversationDate(dateStr) {
        if (!dateStr) return '';
        const date = typeof dateStr === 'number' ? new Date(dateStr * 1000) : new Date(dateStr);
        if (isNaN(date.getTime())) return '';

        if (this.dateIsToday(date)) return this.$FUSdateFormat('h:mm A', date);
        if (this.dateIsYesterday(date)) return this.$t('messaging.yesterday');
        return this.$FUSdateFormat('M/D/YYYY', date);
      },
      dateIsToday(d) {
        const today = new Date();
        return d.getDate() === today.getDate() &&
          d.getMonth() === today.getMonth() &&
          d.getFullYear() === today.getFullYear();
      },
      dateIsYesterday(d) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return d.getDate() === yesterday.getDate() &&
          d.getMonth() === yesterday.getMonth() &&
          d.getFullYear() === yesterday.getFullYear();
      },
    },
  }
</script>
